import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import SettingsLayout from '../../components/SettingsLayout';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { API_URL } from '../../lib/apiBase';
import {
  KeyRound,
  Plus,
  Copy,
  Check,
  Trash2,
  ShieldAlert,
  Terminal,
  Sparkles,
} from 'lucide-react';

const CopyField = ({ value, testId }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) {
      toast.error('Copy failed — select and copy manually');
    }
  };
  return (
    <div className="flex items-stretch gap-2">
      <code className="flex-1 min-w-0 rounded-md border bg-muted/60 px-3 py-2 text-xs font-mono break-all" data-testid={`${testId}-value`}>
        {value}
      </code>
      <Button type="button" variant="outline" size="sm" onClick={copy} data-testid={`${testId}-copy`}>
        {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  );
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (_e) {
    return '—';
  }
};
const fmtRelative = (iso) => {
  if (!iso) return 'never';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  } catch (_e) {
    return '—';
  }
};

const ApiTokensPage = () => {
  const { currentWorkspaceId, currentWorkspace } = useWorkspace();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [endpoint, setEndpoint] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const [revealSecret, setRevealSecret] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);

  const load = useCallback(async () => {
    if (!currentWorkspaceId) return;
    try {
      setLoading(true);
      const [tRes, iRes] = await Promise.all([
        axios.get(`${API_URL}/api/tokens`, { params: { workspace_id: currentWorkspaceId } }),
        axios.get(`${API_URL}/api/mcp/info`, { params: { workspace_id: currentWorkspaceId } }),
      ]);
      setTokens(tRes.data.tokens || []);
      setEndpoint(iRes.data.endpoint || '');
    } catch (e) {
      console.error(e);
      toast.error('Failed to load API tokens');
    } finally {
      setLoading(false);
    }
  }, [currentWorkspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  const createToken = async () => {
    const name = newName.trim();
    if (!name) {
      toast.error('Give the token a name');
      return;
    }
    try {
      setCreating(true);
      const { data } = await axios.post(`${API_URL}/api/tokens`, {
        workspace_id: currentWorkspaceId,
        name,
      });
      setCreateOpen(false);
      setNewName('');
      setRevealSecret({ secret: data.secret, name: data.token.name });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create token');
    } finally {
      setCreating(false);
    }
  };

  const revokeToken = async () => {
    if (!revokeTarget) return;
    try {
      await axios.delete(`${API_URL}/api/tokens/${revokeTarget.id}`);
      toast.success('Token revoked');
      setRevokeTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke token');
    }
  };

  const clientSnippet = endpoint
    ? `{
  "mcpServers": {
    "vibesprint": {
      "url": "${endpoint}",
      "headers": { "Authorization": "Bearer YOUR_TOKEN" }
    }
  }
}`
    : '';

  return (
    <SettingsLayout
      title="API Tokens"
      description="Authenticate with the VibeSprint API and connect MCP clients like Claude, Cursor, and ChatGPT."
    >
      <div className="space-y-8" data-testid="api-tokens-page">
        {/* Tokens section */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Tokens</h2>
            </div>
            <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="new-token-btn">
              <Plus className="h-4 w-4 mr-1.5" /> New token
            </Button>
          </div>

          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
            </div>
          ) : tokens.length === 0 ? (
            <Card className="p-10 text-center" data-testid="tokens-empty">
              <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
                <KeyRound className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="font-semibold">No tokens yet</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
                Create a token to authenticate with the VibeSprint API or connect an MCP client
                to this workspace.
              </p>
              <Button size="sm" className="mt-4" onClick={() => setCreateOpen(true)} data-testid="tokens-empty-create">
                <Plus className="h-4 w-4 mr-1.5" /> New token
              </Button>
            </Card>
          ) : (
            <Card className="divide-y" data-testid="tokens-list">
              {tokens.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-4 p-4" data-testid={`token-row-${t.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{t.name}</span>
                      <code className="text-[11px] font-mono text-muted-foreground">{t.token_preview}</code>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Created {fmtDate(t.created_at)} · Last used {fmtRelative(t.last_used_at)}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-rose-600 hover:text-rose-700 hover:bg-rose-500/10"
                    onClick={() => setRevokeTarget(t)}
                    data-testid={`token-revoke-${t.id}`}
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" /> Revoke
                  </Button>
                </div>
              ))}
            </Card>
          )}

          <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
            <ShieldAlert className="h-3.5 w-3.5" />
            Every token grants access only to <strong className="font-medium">{currentWorkspace?.name || 'this workspace'}</strong>. Treat it like a password.
          </p>
        </section>

        <Separator />

        {/* Connection guide */}
        <section data-testid="connection-guide">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Connect an agent (MCP)
          </h2>
          <Card className="p-5 space-y-5">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                <Terminal className="h-5 w-5" />
              </div>
              <p className="text-sm text-muted-foreground">
                Paste the endpoint and a token into any MCP-compatible client (Claude, Cursor,
                ChatGPT, Copilot, Gemini). No install required — one URL + token works everywhere.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label>Endpoint URL</Label>
              <CopyField value={endpoint || 'Loading…'} testId="guide-endpoint" />
            </div>

            <div className="space-y-1.5">
              <Label>Client config (e.g. claude_desktop_config.json)</Label>
              <div className="flex items-stretch gap-2">
                <pre className="flex-1 min-w-0 overflow-x-auto rounded-md border bg-muted/60 px-3 py-2 text-xs font-mono" data-testid="guide-snippet">
{clientSnippet}
                </pre>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    await navigator.clipboard.writeText(clientSnippet);
                    toast.success('Config copied');
                  }}
                  data-testid="guide-snippet-copy"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Replace <code className="font-mono">YOUR_TOKEN</code> with a token created above.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-primary" /> Try these prompts</Label>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc pl-5">
                <li>“Triage this week’s feedback into Build, Fix, and Ignore.”</li>
                <li>“What’s in Fix Now right now?”</li>
                <li>“Mark the Slack integration as shipped and publish a changelog.”</li>
              </ul>
            </div>
          </Card>
        </section>
      </div>

      {/* Create token dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-token-dialog">
          <DialogHeader>
            <DialogTitle>New API token</DialogTitle>
            <DialogDescription>
              Name it after where you’ll use it, so you can revoke the right one later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-1">
            <Label htmlFor="token-name">Token name</Label>
            <Input
              id="token-name"
              placeholder="e.g. Claude on my laptop"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={60}
              data-testid="token-name-input"
              onKeyDown={(e) => e.key === 'Enter' && createToken()}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createToken} disabled={creating} data-testid="token-create-confirm">
              {creating ? 'Creating…' : 'Create token'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reveal-once dialog */}
      <Dialog open={!!revealSecret} onOpenChange={(o) => !o && setRevealSecret(null)}>
        <DialogContent data-testid="reveal-token-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-500" />
              Copy your token now
            </DialogTitle>
            <DialogDescription>
              This is the only time “{revealSecret?.name}” is shown in full. Store it somewhere safe —
              you can’t view it again, only revoke it.
            </DialogDescription>
          </DialogHeader>
          <div className="py-1">
            {revealSecret && <CopyField value={revealSecret.secret} testId="reveal-secret" />}
          </div>
          <DialogFooter>
            <Button onClick={() => setRevealSecret(null)} data-testid="reveal-done-btn">
              Done — I’ve copied it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke confirm */}
      <AlertDialog open={!!revokeTarget} onOpenChange={(o) => !o && setRevokeTarget(null)}>
        <AlertDialogContent data-testid="revoke-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke “{revokeTarget?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Any client using this token will stop working immediately. Your other tokens are
              unaffected. This can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={revokeToken}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="revoke-confirm-btn"
            >
              Revoke token
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsLayout>
  );
};

export default ApiTokensPage;
